import React from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';

import { cn } from './utils';


/**
 * Boolean toggle per the .pen "Choice & numeric" (vPh8z) toggle row and the
 * DESIGN.md `toggle` token: 44x26 pill track, 20px white knob, 3px inset.
 * On = solid indigo (single accent, NOT the gradient), off = hairline-strong
 * track. Built on @radix-ui/react-switch (keyboard, role=switch, a11y).
 *
 * Controlled: `checked` + `onCheckedChange(next)`. Uncontrolled: `defaultChecked`.
 *
 * `presentational`: render the same pill/knob as a non-interactive `<span>`
 * (no Radix, no controllable state) driven purely by `checked`. Use when an
 * outer element already owns the `role="switch"` semantics and click/keyboard
 * (e.g. ToggleRow) and this is just the visual indicator — avoids the Radix
 * controlled-without-onChange sync loop.
 */
const Toggle = React.forwardRef((props, ref) => {
  const { className, presentational, ...restProps } = props;

  if (presentational) {
    const { checked, ...spanProps } = restProps;
    const state = checked ? 'checked' : 'unchecked';

    return (
      <span
        ref={ref}
        data-state={state}
        className={cn(
          'peer inline-flex h-[26px] w-11 shrink-0 items-center rounded-full p-[3px]',
          'border border-transparent transition-colors',
          'data-[state=checked]:bg-primary data-[state=unchecked]:bg-hairline-strong',
          className,
        )}
        {...spanProps}
      >
        <span
          data-state={state}
          className={cn(
            'pointer-events-none block size-5 rounded-full bg-white shadow-sm transition-transform',
            'data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-0',
          )}
        />
      </span>
    );
  }

  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'peer inline-flex h-[26px] w-11 shrink-0 cursor-pointer items-center rounded-full p-[3px]',
        'border border-transparent outline-none transition-colors',
        'focus-visible:ring-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-hairline-strong',
        className,
      )}
      {...restProps}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-white shadow-sm transition-transform',
          'data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-0',
        )}
      />
    </SwitchPrimitive.Root>
  );
});

Toggle.displayName = 'Toggle';

export { Toggle };
export default Toggle;
